// @flow
import type { ActionForm, SavingState } from '~/core/state/actions/types';
import type { ExtendedFormAttribute } from '../../types';

import * as React from 'react';
import { noop } from 'lodash/fp';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Modal, SideModal } from '~/components/widgets';
import ActionDetail from '../action-detail';

import messages from '../intl';

const ConfirmTitle = styled.div`
    align-items: center;
    display: flex;
    justify-content: center;
    height: 3em;

    & > span {
        color: ${p => p.theme.mainBrandColor};
        font-size: 1.2em;
    }
`;

const ConfirmText = styled.div`
    align-items: center;
    display: flex;
    justify-content: center;
    min-height: 3em;

    & > span {
        font-size: 1.1em;
    }
`;

// if there is an event notification check at least one the settings (push, email or text) is true
const checkIfValidNotification = (form: ActionForm<ExtendedFormAttribute>) => {
    const notificationEvent = form.then.find(event => event.type === 'notification');
    return !notificationEvent
        ? true
        : Object.keys(notificationEvent.settings || {}).some(
              key => notificationEvent.settings && notificationEvent.settings[key]
          );
};

type Props = {
    attemptRemoveAction: string => void,
    cancelForm: noop,
    extendedForm: ?ActionForm<ExtendedFormAttribute>,
    parentRemoveActionState?: ?{ id: string, name: string, deprecated?: boolean },
    parentResetActionState?: noop,
    saveAction: (?string) => void,
    savingState: SavingState
};

type State = {
    actionNameEdited: ?string,
    removeAction: ?{ id: string, name: string, deprecated?: boolean }
};

class ActionCanvas extends React.Component<Props, State> {
    state = {
        actionNameEdited: null,
        removeAction: null
    };

    componentWillUpdate(nextProps: Props) {
        if (this.state.actionNameEdited && !nextProps.extendedForm) {
            this.setState({ actionNameEdited: null });
        }
    }

    componentWillReceiveProps(nextProps: Props) {
        if (nextProps.parentRemoveActionState) {
            this.setState({ removeAction: nextProps.parentRemoveActionState });
        }
    }

    editActionName = (name: string) => this.setState({ actionNameEdited: name });

    hideSideModal = () => {
        this.setState({ actionNameEdited: null });
        this.props.cancelForm();
    };

    removeAction = () => {
        const { attemptRemoveAction, parentResetActionState } = this.props;
        const { removeAction } = this.state;
        removeAction && attemptRemoveAction(removeAction.id);
        this.setState({ removeAction: null });
        parentResetActionState && parentResetActionState();
    };

    showConfirmModal = (actionId: string, name: string, deprecated: boolean = false) =>
        this.setState({ removeAction: { id: actionId, name, deprecated } });

    hideConfirmModal = () => {
        this.setState({ removeAction: null });
        this.props.parentResetActionState && this.props.parentResetActionState();
    };

    render() {
        const { extendedForm, saveAction, savingState } = this.props;
        const { actionNameEdited, removeAction } = this.state;

        const savableForm: boolean =
            savingState !== 'saving' && actionNameEdited !== '' && extendedForm
                ? extendedForm.then.length > 0 && checkIfValidNotification(extendedForm)
                : false;

        return [
            <SideModal
                asyncButton={{
                    disabled: !savableForm,
                    error: savingState === 'failed',
                    onClick: () => saveAction(actionNameEdited),
                    saving: savingState === 'saving'
                }}
                isOpen={!!extendedForm}
                key="actions-side-modal"
                onRequestClose={this.hideSideModal}
                title={messages.sideModalTitle}
            >
                {extendedForm && (
                    <ActionDetail
                        actionNameEdited={actionNameEdited}
                        editActionName={this.editActionName}
                        extendedForm={extendedForm}
                        showConfirmModal={this.showConfirmModal}
                    />
                )}
            </SideModal>,
            <Modal
                isOpen={!!removeAction}
                key="confirm-modal"
                onRequestClose={this.hideConfirmModal}
                onSave={this.removeAction}
                saveText={messages.removeActionButton}
            >
                {removeAction && [
                    <ConfirmTitle key="title">
                        <FormattedMessage
                            {...(removeAction.deprecated
                                ? messages.notFullySupportedTitle
                                : messages.confirmRemoveAction)}
                        />
                    </ConfirmTitle>,
                    <ConfirmText key="content">
                        {removeAction.deprecated ? (
                            <FormattedMessage {...messages.notFullySupportedContent} />
                        ) : (
                            removeAction.name
                        )}
                    </ConfirmText>
                ]}
            </Modal>
        ];
    }
}

export default ActionCanvas;



// WEBPACK FOOTER //
// ./src/components/widgets/actions/entry/action-canvas/component.js